import type { StopwatchCommand, StopwatchRun } from "@hisani/stopwatch";
import { Button } from "@hisani/ui";
import {
  Flag,
  MessageSquarePlus,
  Pause,
  Play,
  RotateCcw,
  Save,
  Square,
} from "lucide-react";
import { useTranslations } from "next-intl";
import React from "react";

export function StopwatchTimerControls({
  run,
  splitDistanceMeters,
  onCommand,
  onReset,
  onComment,
  onSave,
  isSaving,
  saveDisabled,
  saveLabel,
}: {
  readonly run: StopwatchRun;
  readonly splitDistanceMeters: number;
  readonly onCommand: (id: string, command: StopwatchCommand) => void;
  readonly onReset: () => void;
  readonly onComment: () => void;
  readonly onSave: () => void;
  readonly isSaving: boolean;
  readonly saveDisabled: boolean;
  readonly saveLabel: string;
}) {
  const t = useTranslations("Stopwatch");

  if (run.status === "idle") {
    return (
      <div className="mt-1 @min-[40rem]/stopwatch:mt-5">
        <Button
          variant="accent"
          className="w-full @min-[40rem]/stopwatch:min-h-12 @min-[40rem]/stopwatch:px-6 @min-[40rem]/stopwatch:text-base"
          onClick={() => onCommand(run.id, { type: "START" })}
        >
          <Play className="me-2 size-5 fill-current" aria-hidden="true" />
          {t("start")}
        </Button>
      </div>
    );
  }

  if (run.status === "running") {
    return (
      <div className="mt-1 grid grid-cols-3 gap-2 @min-[40rem]/stopwatch:mt-5">
        <Button
          variant="secondary"
          className="px-2 @min-[40rem]/stopwatch:px-4"
          onClick={() => onCommand(run.id, { type: "PAUSE" })}
        >
          <Pause className="me-1.5 size-4 fill-current" aria-hidden="true" />
          {t("pause")}
        </Button>
        <Button
          variant="accent"
          className="px-2 @min-[40rem]/stopwatch:px-4"
          onClick={() =>
            onCommand(run.id, {
              type: "RECORD_SECTIONAL",
              distanceMeters: splitDistanceMeters,
            })
          }
        >
          <Flag className="me-1.5 size-4" aria-hidden="true" />
          {t("recordSplit")}
        </Button>
        <Button
          variant="outline"
          className="px-2 @min-[40rem]/stopwatch:px-4"
          onClick={() => onCommand(run.id, { type: "STOP" })}
        >
          <Square className="me-1.5 size-4 fill-current" aria-hidden="true" />
          {t("stop")}
        </Button>
      </div>
    );
  }

  if (run.status === "paused") {
    return (
      <div className="mt-1 grid grid-cols-3 gap-2 @min-[40rem]/stopwatch:mt-5">
        <Button
          variant="accent"
          className="px-2 @min-[40rem]/stopwatch:px-4"
          onClick={() => onCommand(run.id, { type: "RESUME" })}
        >
          <Play className="me-1.5 size-4 fill-current" aria-hidden="true" />
          {t("resume")}
        </Button>
        <Button
          variant="outline"
          className="px-2 @min-[40rem]/stopwatch:px-4"
          onClick={() => onCommand(run.id, { type: "STOP" })}
        >
          <Square className="me-1.5 size-4 fill-current" aria-hidden="true" />
          {t("stop")}
        </Button>
        <Button
          variant="outline"
          className="px-2 @min-[40rem]/stopwatch:px-4"
          onClick={onReset}
        >
          <RotateCcw className="me-1.5 size-4" aria-hidden="true" />
          {t("reset")}
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-1 grid grid-cols-3 gap-2 @min-[40rem]/stopwatch:mt-5">
      <Button
        variant="outline"
        className="w-full"
        onClick={onReset}
        disabled={isSaving}
      >
        <RotateCcw className="me-1.5 size-4" aria-hidden="true" />
        {t("reset")}
      </Button>
      <Button
        variant="outline"
        className="w-full px-2"
        onClick={onComment}
        disabled={saveDisabled}
      >
        <MessageSquarePlus
          className="me-1.5 size-4 shrink-0"
          aria-hidden="true"
        />
        {t("comment")}
      </Button>
      <Button
        variant="accent"
        className="w-full"
        onClick={onSave}
        loading={isSaving}
        disabled={saveDisabled}
        aria-label={saveLabel}
      >
        {isSaving ? null : (
          <Save className="me-1.5 size-4" aria-hidden="true" />
        )}
        {t("save")}
      </Button>
    </div>
  );
}
